import { Container } from '@material-ui/core'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import MUIDataTable from "mui-datatables"
import { useSnackbar } from 'notistack'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import PageTitle from '../../../components/ui/PageTitleUI'
import { useAuth } from '../../../hooks/auth'
import MvmApi from '../../../services/MvmApi'
import { DataFormat } from '../../../utils/componentUtils'
import utilsDate from '../../../utils/Date'
import permissions from '../../../utils/Permissions'
import { useStyles } from './Style'

const PendingTransportOrderMonitor: React.FC = () => {
    const history = useHistory();
    const classes = useStyles();
    const { t: _t } = useTranslation("General");
    const { t } = useTranslation("Mvm");
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [mvmId, setMvmId] = React.useState(0);
    const { user, hasPermission } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [openBackdrop, setOpenBackdrop] = React.useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.MVM.PendingTransOrder))
            history.replace('NoPermission');
    }, [history, user]);

    const { isLoading, isFetching, data } = useQuery('getPendingTransportOrder', async () => {
        return await MvmApi.getPendingTransportOrder(+user.company.plantID);
    } ,{
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 1000 * 60, //calculado em milisegundos - Propriedade para setar qto tempo o cache fica válido
        refetchInterval: 1000 * 30, //calculado em milisegundos - Propriedade para setar qto tempo vai buscar o cache novamente
    });

    const handleConfirmation = async () => {
        setDialogOpen(false);
        setOpenBackdrop(true);
        MvmApi.autoryzePlantEntry({MvmId: mvmId})
            .then(result => async () => {
                enqueueSnackbar(t("entryAuthorizedSuccess"), { variant: 'success' });
            }).finally(() => {
                setOpenBackdrop(false);
            });
      };

    const handleDialogClickClose = () => {
        setDialogOpen(false);
      };

    const columns = [
        {
            name: 'mvmNumber',
            label: t("mvmNumber"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value : any, tableMeta : any, updateValue : any) => (
                    <Link href="#" onClick={() => startPendingTransportOrder(value)}>{value}</Link>
                )
            }
            
        },
        {
            name: 'updatedAt',
            label: _t("date"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: DataFormat
            }
        },
        {
            name: 'pendingTransportOrderResolutionUser',
            label: t("blockedInAnalysis"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                })
            }
        },
        {
            name: 'pendingTransportOrderResolutionUser',
            label: t("blockUser"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                })
            }
        }
    ]

    const options = {
        textLabels: {
            body: {
                noMatch: _t('noMatch'),
                toolTip: "",
            },
            pagination: {
                next: _t("next"),
                previous: _t("previous"),
                rowsPerPage: _t("rowsPerPage") + ":",
                displayRows: _t("of"),
            }
        }
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''                    
                }
            },
            MuiTableCell: {
                root: {
                    paddingLeft: 4,
                    paddingRight: 4,
                    paddingBottom: 10,
                    paddingTop: 10
                },
                head: {
                    height: 52
                }
            }
        }
    })

    function startPendingTransportOrder(mvmNumber : number) {
        MvmApi.startPendingTransportOrder(mvmNumber)
            .then(() => (enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' })));
    }

    return (
        <Container maxWidth={false} component="main">
            <Backdrop className={classes.backdrop} open={openBackdrop || isLoading || isFetching}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <PageTitle title={t("transportOrderTitle")} />
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClickClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                {_t("authorization")}
                </DialogTitle>
                <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {t("authorizePlantEntryDialogText")}
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                <Button onClick={handleDialogClickClose}>{_t("cancelDeleteTextButton")}</Button>
                <Button onClick={() => handleConfirmation()} autoFocus>{t("authorize")}</Button>
                </DialogActions>
            </Dialog>

            <div style={{marginTop: "0.2rem", marginBottom: "0.3rem", display: "grid"}}>
                <span style={{fontWeight:"bold",fontSize:"0.8rem"}}> {_t("lastUpdate") + utilsDate.TimeByCompleteDate(new Date())} </span>
                <span>{_t("totalRecords") + (data ? data.length : 0)}</span>
            </div>

            {
                <MuiThemeProvider theme={myTheme}>
                    <MUIDataTable
                        title=""
                        data={data != null && data.length >0? data : []}
                        columns={columns} 
                        options={{
                            ...options,
                            serverSide: true,
                            pagination: false,
                            sort: false,
                            selectToolbarPlacement: "none",
                            filter: false,
                            search: false,
                            download: false,
                            print: false,
                            viewColumns: false,
                            selectableRows: 'none',
                        }}
                    />
                </MuiThemeProvider>
            }
        </Container>
    )
}
export default PendingTransportOrderMonitor;


